import React, { useMemo } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import _ from "lodash";

export interface YearlyOrderData {
  year: string | number;
  month: string | number;
  warehouseId: string | number;
  warehouseName: string;
  count: string | number;
}

interface ChartProps {
  data: YearlyOrderData[];
  type?: "line" | "bar"; // Tipo di grafico da visualizzare
}

// Nomi dei mesi per l'asse X
const monthNames = [
  "Gen",
  "Feb",
  "Mar",
  "Apr",
  "Mag",
  "Giu",
  "Lug",
  "Ago",
  "Set",
  "Ott",
  "Nov",
  "Dic",
];

// Componente per un tooltip personalizzato
const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    const monthIndex = parseInt(label) - 1; // Converte da 1-based a 0-based
    const monthName = monthNames[monthIndex] || label;

    return (
      <div className="bg-white p-3 border border-gray-200 shadow-md rounded-md">
        <p className="font-semibold text-gray-700">{monthName}</p>
        {payload.map((entry: any, index: number) => (
          <div key={`item-${index}`} className="flex items-center mt-1">
            <div
              className="w-3 h-3 mr-2 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-sm">
              {entry.name}: <span className="font-medium">{entry.value}</span>
            </span>
          </div>
        ))}
      </div>
    );
  }

  return null;
};

// Formattatore per l'asse X (mostra il nome del mese)
const formatXAxis = (monthStr: string | number) => {
  const month = parseInt(monthStr.toString(), 10);
  const monthIndex = month - 1;
  return monthNames[monthIndex] || month.toString();
};

const YearlyOrdersChart: React.FC<ChartProps> = ({ data, type = "line" }) => {
  // Elabora i dati per il grafico
  const processedData = useMemo(() => {
    if (!data || data.length === 0) {
      return [];
    }

    // Converti stringhe in numeri dove necessario
    const normalizedData = data.map((item) => ({
      year: typeof item.year === "string" ? parseInt(item.year, 10) : item.year,
      month:
        typeof item.month === "string" ? parseInt(item.month, 10) : item.month,
      warehouseId: item.warehouseId,
      warehouseName: item.warehouseName,
      count:
        typeof item.count === "string" ? parseInt(item.count, 10) : item.count,
    }));

    // Raggruppa i dati per mese
    const groupedByMonth = _.groupBy(normalizedData, "month");

    // Ottieni tutti i magazzini
    const allWarehouses = _.uniqBy(normalizedData, "warehouseId").map(
      (item) => ({
        id: item.warehouseId,
        name: item.warehouseName,
      })
    );

    // Crea un array con tutti i mesi dell'anno (1-12)
    const chartData = [];

    for (let month = 1; month <= 12; month++) {
      const monthData = groupedByMonth[month] || [];
      const result: any = { month }; // Usa il numero del mese come chiave

      // Aggiungi il conteggio per ogni magazzino
      allWarehouses.forEach((warehouse) => {
        const warehouseData = monthData.find(
          (item) => item.warehouseId.toString() === warehouse.id.toString()
        );

        // Se non ci sono dati per questo magazzino in questo mese, imposta a 0
        result[warehouse.name] = warehouseData ? warehouseData.count : 0;
      });

      chartData.push(result);
    }

    return chartData;
  }, [data]);

  // Genera colori unici per ogni magazzino
  const warehouseColors = useMemo(() => {
    if (!data || data.length === 0) {
      return {};
    }

    const colors = [
      "#8884d8", // viola
      "#82ca9d", // verde
      "#ffc658", // giallo
      "#ff7300", // arancione
      "#0088fe", // blu
      "#00C49F", // turchese
      "#FFBB28", // giallo chiaro
      "#FF8042", // arancione chiaro
      "#a4de6c", // verde chiaro
      "#d0ed57", // lime
      "#7A4DFF", // viola intenso
      "#FF4D4D", // rosso
    ];

    const uniqueWarehouses = _.uniqBy(data, "warehouseName").map(
      (item) => item.warehouseName
    );

    // Associa i magazzini ai colori
    return uniqueWarehouses.reduce((acc, warehouse, index) => {
      acc[warehouse] = colors[index % colors.length];
      return acc;
    }, {} as Record<string, string>);
  }, [data]);

  // Ottieni i nomi dei magazzini
  const warehouseNames = useMemo(() => {
    if (!data || data.length === 0) {
      return [];
    }
    return _.uniqBy(data, "warehouseName").map((item) => item.warehouseName);
  }, [data]);

  // Se non ci sono dati, mostra un messaggio
  if (!data || data.length === 0 || warehouseNames.length === 0) {
    return (
      <div className="flex justify-center items-center h-full w-full">
        <p className="text-gray-500">
          Nessun dato disponibile per il periodo selezionato
        </p>
      </div>
    );
  }

  // Rendering condizionale basato sul tipo di grafico
  if (type === "bar") {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={processedData}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="month"
            padding={{ left: 10, right: 10 }}
            tickFormatter={formatXAxis}
            tick={{ fontSize: 12 }}
          />
          <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {warehouseNames.map((name) => (
            <Bar
              key={name}
              dataKey={name}
              name={name}
              fill={warehouseColors[name]}
              isAnimationActive={true}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    );
  }

  // Grafico a linee (predefinito)
  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart
        data={processedData}
        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="month"
          padding={{ left: 10, right: 10 }}
          tickFormatter={formatXAxis}
          tick={{ fontSize: 12 }}
        />
        <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
        <Tooltip content={<CustomTooltip />} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        {warehouseNames.map((name) => (
          <Line
            key={name}
            type="monotone"
            dataKey={name}
            name={name}
            stroke={warehouseColors[name]}
            strokeWidth={2}
            dot={{ r: 4 }}
            activeDot={{ r: 6 }}
            isAnimationActive={true}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
};

export default YearlyOrdersChart;
